import { addDaysYMD, chicagoHHMM, chicagoMidnightMs, chicagoYMD, ymdWeekday } from "./chicagoTime";

// ---- Ten Seconds to Undo (Henry, 09/16/2026). Mark Complete no longer asks
// "are you sure?"; it completes at once and offers a short way back instead.

// How long the page keeps an Undo on screen after Mark Complete. The window
// lives in the page's memory only: nothing is stamped on the document and the
// write is NOT held back, so a refresh simply ends the window. Holding the
// write instead would leave the open house silently un-completed if the person
// closed the tab.
export const UNDO_WINDOW_MS = 10000;

export function deriveStatus(oh) {
  if (oh?.status === "Cancelled") return "Cancelled";
  // An explicit "done" counts as well as feedback. Completion used to be
  // inferred purely from feedback existing, which left an open house that
  // happened but produced nothing worth writing down stuck on "Scheduled".
  if (oh?.status === "Completed" || oh?.feedback) return "Completed";
  return "Scheduled";
}

// Marked done with nothing to report. Henry's call 2026-08-02 was "closed means
// closed — no recap, no feedback afterwards, no restarting it". As of
// 09/16/2026 that holds from ten seconds after the click: an Undo bar offers
// the way back inside UNDO_WINDOW_MS, and afterwards a Team Lead or the owner
// can Reopen (canReopenOpenHouse). What this helper answers has not changed:
// the row still goes quiet (no feedback, no recap), leaving View Sign-Ins,
// Delete and, for a Team Lead or the owner, Reopen.
export function isClosedNoFeedback(oh) {
  return deriveStatus(oh) === "Completed" && !oh?.feedback;
}

// Is this open house closed to new kiosk sign-ins? Cancelled or complete —
// and "complete" means exactly what deriveStatus means by it, so an open house
// completed by feedback is closed too. Until 09/16/2026 the iPad only refused a
// Cancelled open house and a completed one kept taking sign-ins; Henry's call
// is that marking complete closes the iPad, the way staff already assumed it
// worked. Undo and Reopen both open it again, because both write the status
// back to "Scheduled".
// A missing record is the caller's own check (see pages/oh-checkin.jsx, which
// fails on `!oh` before it asks this).
export function isKioskClosed(openHouse) {
  return deriveStatus(openHouse) !== "Scheduled";
}

// Is the page's ten second window ({ id, endsAt }) still open, and is it the
// window for THIS open house? Both pages ask this themselves before an Undo
// writes, rather than trusting that the bar has already hidden itself: the bar
// only redraws four times a second, and a bar in the wrong place must never be
// able to put back a different open house (review fix, 09/16/2026).
export function undoWindowOpenFor(undo, openHouseId, now = Date.now()) {
  if (!undo || openHouseId === undefined || openHouseId === null) return false;
  return String(undo.id) === String(openHouseId) && now < undo.endsAt;
}

// What to say when an Undo could not be saved. While the ten seconds are still
// running the page puts the bar back, so "Try again" is true. Once they are
// over there is no bar left to press, so the message says what to do instead:
// a Team Lead or the owner has Reopen Open House, everyone else asks one
// (review fix, 09/16/2026).
// `rowMayBeFilteredOut`: the page has filters that can hide the row. The Open
// Houses page does: on Upcoming the row has left the list once the ten seconds
// are over, so its Team Lead is sent to the lists that always show it (polish
// round, 09/16/2026). The deal tab lists every open house, so it points at the
// row itself.
export function undoFailedMessage({ windowOpen, mayReopen, rowMayBeFilteredOut = false }) {
  if (windowOpen) return "Could not undo. Try again.";
  if (!mayReopen) return "Could not undo. Ask a Team Lead to reopen it.";
  return rowMayBeFilteredOut
    ? "Could not undo. Use Reopen Open House from the All or Completed list."
    : "Could not undo. Use Reopen Open House in the row's ⋮ menu.";
}

export function sortOpenHouses(rows) {
  return [...rows].sort((a, b) => {
    const aIsScheduled = deriveStatus(a) === "Scheduled";
    const bIsScheduled = deriveStatus(b) === "Scheduled";
    if (aIsScheduled !== bIsScheduled) return aIsScheduled ? -1 : 1;
    if (aIsScheduled) return (a.date || "").localeCompare(b.date || "");
    return (b.date || "").localeCompare(a.date || "");
  });
}

// Pre-Market included so "sneak peek" open houses can be scheduled before
// the listing goes fully live (2026-07-27, Henry's call after a Pre-Market
// listing was missing from the schedule dropdown).
const ELIGIBLE_STAGES = new Set(["Pre-Market", "Coming Soon", "Active"]);

// A listing whose seller said Not Allowed (deal.openHousePolicy.allowed === false, the
// Open Houses switch on the deal page, 09/16/2026) is hidden here too: flip the switch
// to schedule. Not Set (missing or null) stays schedulable, today's behaviour.
// The test is inlined rather than imported from utils/openHousePolicy.js because this
// file must stay import-free (public-feedback.jsx loads it before sign-in).
// Soft-deleted (deletedAt set) and archived deals are never offered either: the
// sandbox's deleted "1 Husk Test Lane" still showed here (09/25/2026).
function isLiveDeal(d) {
  return !(Number(d?.deletedAt) > 0) && d?.archived !== true;
}

export function filterEligibleListings(deals) {
  return (deals || []).filter(
    (d) =>
      d?.dealtype === "Seller" &&
      ELIGIBLE_STAGES.has(d?.dealStage) &&
      isLiveDeal(d) &&
      d?.openHousePolicy?.allowed !== false
  );
}

// Up For Grabs is stricter than the schedule dropdown: only listings a guest
// could actually walk through soon — Coming Soon or Active, and not deleted.
// Pre-Market stays OUT here (it flooded the picker with ~19 parked listings and
// soft-deleted husks — 39 options where only 16 were real; 2026-08-27, Henry:
// "should only be anything coming soon or active. i want to make it easier to
// find"). The schedule dropdown keeps Pre-Market for sneak peeks (his 7/27 call).
const UP_FOR_GRABS_STAGES = new Set(["Coming Soon", "Active"]);

export function filterUpForGrabsListings(deals) {
  return (deals || []).filter(
    (d) =>
      d?.dealtype === "Seller" &&
      UP_FOR_GRABS_STAGES.has(d?.dealStage) &&
      isLiveDeal(d) &&
      // The seller's Not Allowed switch (see filterEligibleListings above).
      d?.openHousePolicy?.allowed !== false &&
      // Listing Agent Only (10/05/2026): nobody else hosts it, so it is never up for grabs. Inlined for
      // the same import-free reason as the line above (openHousePolicy.js isListingAgentOnly).
      d?.openHousePolicy?.audience !== "listingAgent",
  );
}

// 12:00 AM Minnesota time starting the Monday of the week `now` falls in.
function startOfCurrentWeekMs(now) {
  const today = chicagoYMD(now);
  const sinceMonday = (ymdWeekday(today) + 6) % 7; // Mon=0 … Sun=6
  return chicagoMidnightMs(addDaysYMD(today, -sinceMonday));
}

function postedAtMs(o) {
  const t = o?.postedAt ?? o?.createdAt;
  if (t?.toDate) return t.toDate().getTime(); // Firestore Timestamp
  if (typeof t === "number") return t;
  if (Number(t?.seconds)) return Number(t.seconds) * 1000;
  return null;
}

// A slot is for the week it was posted in: it lives through that Sunday night
// and stops showing when Monday starts. 2026-08-31, Henry: "after sunady.
// eveyting that was up ofr grabs goes away." Nothing is deleted — the board,
// the dashboard card and the approval queue just stop listing it.
// A missing/unresolved timestamp never expires: serverTimestamp() reads back
// as null on the posting client until the write round-trips.
export function isExpiredOpportunity(o, now = new Date()) {
  const ms = postedAtMs(o);
  if (ms === null) return false;
  return ms < startOfCurrentWeekMs(now);
}

// A claimable slot must point at a house you can still hold an open house on.
// Without this, an Open opportunity outlived its listing: live data 2026-08-27
// had an open slot for a deal already Under Contract. Cancelled/Filled slots
// are excluded here too — callers show open, claimable slots only. Slots from
// past weeks are gone as well (isExpiredOpportunity above).
export function liveOpenOpportunities(opportunities, deals, now = new Date()) {
  const byId = new Map(
    (deals || []).map((d) => [String(d?.createdat ?? d?.id), d]),
  );
  return (opportunities || []).filter((o) => {
    if (o?.status !== "Open") return false;
    if (isExpiredOpportunity(o, now)) return false;
    const deal = byId.get(String(o?.dealId));
    return !!deal && filterUpForGrabsListings([deal]).length === 1;
  });
}

export function formatTimeRange(start, end) {
  if (!start || !end) return "—";
  return `${formatTime(start)} – ${formatTime(end)}`;
}

// "14:00" -> "2:00pm". Exported so the duplicate open house warning can spell a time the same
// way the table does (openHouseClash.js), instead of keeping its own copy of the rule.
export function formatTime(t) {
  const [hStr, mStr] = String(t).split(":");
  const h = Number(hStr);
  if (isNaN(h)) return t;
  const period = h >= 12 ? "pm" : "am";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${(mStr || "00").padStart(2, "0")}${period}`;
}

export function generateFeedbackToken() {
  const bytes = new Uint8Array(16); // 16 bytes = 128 bits of entropy
  window.crypto.getRandomValues(bytes);
  // Base64url-encode (URL-safe, no padding)
  let b64 = btoa(String.fromCharCode(...bytes));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Which open houses a given user may see in their list. Admin/Supervisor see all.
// Everyone else sees ones they host OR that sit on a listing they own
// (listingAgentId), with a hostingAgentEmail fallback for OH docs created before
// the hosting/listing id fields existed.
export function visibleOpenHousesFor(openHouses, user) {
  if (!user) return [];
  if (user.role === "Admin" || user.role === "Supervisor") return openHouses || [];
  const uid = user.id;
  const email = (user.email || "").toLowerCase();
  return (openHouses || []).filter(
    (oh) =>
      oh.hostingAgentId === uid ||
      oh.listingAgentId === uid ||
      (oh.hostingAgentEmail && email && oh.hostingAgentEmail.toLowerCase() === email),
  );
}

// Decide whether to send the listing agent a copy of an open-house email and
// to whom. Pure + synchronous so it is easy to unit-test. Returns the recipient
// { email, name } to send to, or null to skip (no email, or the agent is already
// being emailed in this flow — deduped against excludeEmails).
export function chooseListingAgentRecipient({
  agentEmail,
  agentName,
  excludeEmails = [],
}) {
  const email = String(agentEmail || "").trim();
  if (!email) return null;
  const lower = email.toLowerCase();
  const excluded = excludeEmails
    .map((e) => String(e || "").trim().toLowerCase())
    .filter(Boolean);
  if (excluded.includes(lower)) return null;
  return { email, name: String(agentName || "").trim() };
}

// ---- Column sorting for the Open Houses table (Henry, 2026-09-10: "I should
// be able to sort here and see every single open house ever scheduled").
// `sort` is { key, dir } or null; null keeps the classic order (upcoming
// first, then history newest-first) from sortOpenHouses above.
export const SORT_KEYS = ["property", "date", "agent", "status", "feedback", "leads"];

const STATUS_RANK = { Scheduled: 0, Completed: 1, Cancelled: 2 };
const lower = (v) => String(v || "").trim().toLowerCase();

// Rows with nothing to sort on (no date, no feedback…) always sink to the
// bottom, whichever direction is chosen — an empty cell is never "first".
function sortValue(oh, key, leadCounts) {
  switch (key) {
    case "property":
      return lower((oh?.propertyAddress || "").split(",")[0]) || null;
    case "date":
      return oh?.date ? `${oh.date} ${oh.startTime || ""}` : null;
    case "agent":
      return lower(oh?.hostingAgentName) || null;
    case "status":
      return STATUS_RANK[deriveStatus(oh)] ?? 9;
    case "feedback":
      return oh?.feedback ? Number(oh.feedback.groups) || 0 : null;
    case "leads":
      return (leadCounts && leadCounts.get ? leadCounts.get(String(oh?.id)) : 0) || 0;
    default:
      return null;
  }
}

export function sortOpenHousesBy(rows, sort, { leadCounts } = {}) {
  if (!sort || !SORT_KEYS.includes(sort.key)) return sortOpenHouses(rows || []);
  const dir = sort.dir === "desc" ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const va = sortValue(a, sort.key, leadCounts);
    const vb = sortValue(b, sort.key, leadCounts);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else cmp = String(va).localeCompare(String(vb));
    if (cmp !== 0) return cmp * dir;
    // Tie-break: newest first, so equal names/statuses still read as history.
    return (b?.date || "").localeCompare(a?.date || "");
  });
}

// A header click cycles: off → first direction → the other → off. Date starts
// newest-first (what "show me everything" usually means); text columns A→Z.
export function nextSort(current, key) {
  const first = key === "date" || key === "leads" || key === "feedback" ? "desc" : "asc";
  if (!current || current.key !== key) return { key, dir: first };
  if (current.dir === first) return { key, dir: first === "asc" ? "desc" : "asc" };
  return null;
}

// "date:desc" <-> { key, dir } for the per-user remembered setting.
export function parseSort(str) {
  const [key, dir] = String(str || "").split(":");
  if (!SORT_KEYS.includes(key)) return null;
  return { key, dir: dir === "desc" ? "desc" : "asc" };
}
export function serializeSort(sort) {
  return sort && SORT_KEYS.includes(sort.key) ? `${sort.key}:${sort.dir === "desc" ? "desc" : "asc"}` : "";
}

// ---- Bulk Mark Complete (Henry, 2026-09-10: "there should be a way to do a
// mass mark as complete"). Only open houses that already happened and are
// still Scheduled can be closed in bulk — a future one should be cancelled
// or edited, not marked done.
// `now` is a Date or ms, read on the Minnesota clock. An open house later today is NOT past yet.
export function canBulkComplete(oh, now = new Date()) {
  if (deriveStatus(oh) !== "Scheduled") return false;
  const d = String(oh?.date || "");
  if (!d) return false;
  const todayIso = chicagoYMD(now);
  if (d < todayIso) return true;
  if (d > todayIso) return false;
  const end = String(oh?.endTime || oh?.startTime || "");
  const hhmm = chicagoHHMM(now);
  return !!end && end <= hhmm;
}
